import React, { useEffect, useReducer } from 'react';
import { connect } from 'react-redux';
import {
  getProjectRenderingStatus,
  getPublishedProjectMediaImageUuid,
} from 'cards/util/digitalSaveTheDate';
import { AppDispatch, RootState } from 'reducers/index';
import { getProjectUUID } from 'cards/selectors/cardProjectSelector';
import { hideModal as hideModalAction } from 'cards/actions/modalActions';
import { getImageUrl } from 'paper/utils/helpers';
import cx from 'classnames';
import styles from 'cards/components/common/modals/Digital/ShareStepModals/styles.module.less';
import * as ShareModalUI from './ShareModalUI';

import { StepModalType } from './types';

const FIVE_MINUTE_TIMEOUT = 60000;
const PROCESSED_SUCCESS = 'PROCESSED_SUCCESS';
const PROCESSED_FAILED = 'PROCESSED_FAILED';

// Modal initial state
const DEFAULT_STATE = {
  mediaFileUrl: null,
  timedOut: false,
  step: 0,
  renderFail: null,
  percentComplete: 0,
  cancelStatus: false,
};

// modal state reducer
function reducer(state: any, action: any) {
  switch (action.type) {
    case 'update_percent':
      return {
        ...state,
        percentComplete: action.payload,
        isReady: action.payload === 100,
      };
    case 'timed_out':
      return {
        ...state,
        timedOut: true,
      };
    case 'retry':
      return {
        ...state,
        percentComplete: 0,
        timedOut: false,
        cancelStatus: false,
      };
    case 'file_available':
      return {
        ...state,
        mediaFileUrl: action.payload,
      };
    case 'file_downloaded':
      return {
        ...state,
        hasDownloaded: true,
      };
    case 'step1':
      return {
        ...state,
        step: 1,
      };
    case 'step2':
      return {
        ...state,
        step: 2,
      };
    case 'cancel':
      return {
        ...state,
        cancelStatus: true,
      };
    case 'error':
      return {
        ...state,
        renderFail: true,
      };
    case 'close_error':
      return {
        ...state,
        renderFail: false,
      };
    default:
      throw new Error();
  }
}

const DownloadImage = (props: StepModalType): JSX.Element => {
  const { previewComponent, projectUUID, imageStatus, hideModal } = props;
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);
  let interval: any;
  const { mediaFileUrl, timedOut, step, percentComplete, cancelStatus, renderFail } = state;

  const downLoadImageFromLink = (url: string) => {
    const element = document.createElement('a');
    element.setAttribute('href', `${window.location.protocol}${url}?cd=attachment`);
    element.setAttribute('download', 'Digital Save The Date Image');
    document.body.append(element);
    element.click();
    element.remove();
  };

  const handleButtonClick = (url: string) => {
    if (step === 2) {
      hideModal();
    } else {
      downLoadImageFromLink(url);
      dispatch({ type: 'step2' });
    }
  };

  // if it calls with 'status' parameter - it will start the image download and display a modal StepOne
  const onReceiveImage = (status?: string | undefined) => {
    getPublishedProjectMediaImageUuid({ projectUUID })
      .then(response => {
        if (status) {
          dispatch({
            type: 'file_available',
            payload: getImageUrl(response.image_id),
          });
          downLoadImageFromLink(getImageUrl(response.image_id));
          dispatch({ type: 'step2' });
        } else {
          setTimeout(() => {
            dispatch({ type: 'update_percent', payload: 100 });
            dispatch({
              type: 'file_available',
              payload: getImageUrl(response.image_id),
            });
            clearInterval(interval);
          }, 1000);
        }
      })
      .catch(err => console.log(err));
  };

  const imageRenderLoop = () => {
    let imageRendered = false;
    let percentage = 0;

    if (step < 3) {
      const { async_request_status: requestStatus, async_request_uuid: requestUuid } = imageStatus;
      if (requestStatus !== PROCESSED_SUCCESS) {
        dispatch({ type: 'step1' });
        // set timer to re-fetch the image render every 5 seconds
        interval = setInterval(() => {
          if (percentage < 90) {
            const randomNumber = Math.floor(Math.random() * Math.floor(8));
            dispatch({ type: 'update_percent', payload: percentage + randomNumber });
            percentage += randomNumber;
          }
          if (!cancelStatus) {
            getProjectRenderingStatus(requestUuid)
              .then((res: any) => {
                if (res.async_request_status === PROCESSED_SUCCESS) {
                  imageRendered = true;
                  clearInterval(interval);
                  onReceiveImage();
                } else if (res.async_request_status === PROCESSED_FAILED) {
                  clearInterval(interval);
                  dispatch({ type: 'error' });
                }
              })
              .catch(err => console.log(err));
          } else {
            clearInterval(interval);
          }
        }, 5000);
      } else {
        imageRendered = true;
        onReceiveImage(requestStatus);
      }

      // timeout after 5 minutes
      setTimeout(() => {
        clearInterval(interval);
        if (!imageRendered) {
          dispatch({ type: 'timed_out' });
        }
      }, FIVE_MINUTE_TIMEOUT);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    if (!cancelStatus && imageStatus) {
      imageRenderLoop();
      return () => {
        clearInterval(interval);
      };
    }
    return () => controller.abort();
  }, [cancelStatus, imageStatus]);

  return (
    <div className={cx(styles.downloadImagedWrapper, { [styles.retryModal]: cancelStatus })}>
      {timedOut ||
        (cancelStatus && (
          <ShareModalUI.TimedOut
            imageRenderLoop={() => {
              imageRenderLoop();
              dispatch({ type: 'retry' });
            }}
          />
        ))}
      {renderFail && (
        <ShareModalUI.RenderFail
          hideModal={() => {
            hideModal();
            dispatch({ type: 'close_error' });
          }}
        />
      )}
      {step === 1 && !cancelStatus && !renderFail && (
        <ShareModalUI.StepOne
          mediaFileUrl={mediaFileUrl}
          handleButtonClick={handleButtonClick}
          percentComplete={percentComplete}
          cancelRequest={() => {
            dispatch({ type: 'cancel' });
          }}
        />
      )}
      {step === 2 && !timedOut && !renderFail && (
        <ShareModalUI.StepTwo
          mediaFileUrl={mediaFileUrl}
          previewComponent={previewComponent}
          handleButtonClick={handleButtonClick}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  projectUUID: getProjectUUID(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  hideModal: () => dispatch(hideModalAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(DownloadImage);
